import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  LayoutDashboard, Calendar, FileText, Image as ImageIcon, Users, ShoppingBag,
  Star, Mail, Inbox, Settings as SettingsIcon, Menu,
  Loader2, Pencil, Plus, Send, Download, Sparkles, Upload, Building2,
  GripVertical, Trash2, StarOff,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";

type StudioRow = Database["public"]["Tables"]["studios"]["Row"];
type AddonRow = Database["public"]["Tables"]["addons"]["Row"];

const IMAGE_CATEGORIES = ["Interior", "Equipment", "Exterior", "Detail", "Other"] as const;
type ImageCategory = typeof IMAGE_CATEGORIES[number];

type ImageItem = {
  url: string;
  thumb_url?: string;
  alt?: string;
  title?: string;
  category?: ImageCategory;
  featured?: boolean;
  variants?: { url: string; width: number; type?: string }[];
};

const adminNav = [
  { label: "Dashboard", to: "/admin", icon: LayoutDashboard },
  { label: "Bookings", to: "/admin/bookings", icon: Calendar },
  { label: "Studios", to: "/admin/studios", icon: Building2 },
  { label: "Email Log", to: "/admin/emails", icon: Send },
  { label: "Blog", to: "/admin/blog", icon: FileText },
  { label: "Gallery", to: "/admin/gallery", icon: ImageIcon },
  { label: "Team", to: "/admin/team", icon: Users },
  { label: "Products", to: "/admin/products", icon: ShoppingBag },
  { label: "Testimonials", to: "/admin/testimonials", icon: Star },
  { label: "Subscribers", to: "/admin/subscribers", icon: Mail },
  { label: "Contact", to: "/admin/contact", icon: Inbox },
  { label: "Settings", to: "/admin/settings", icon: SettingsIcon },
  { label: "Import from WP", to: "/admin/wp-import", icon: Download },
];

const Sidebar = ({ open, toggle }: { open: boolean; toggle: () => void }) => {
  const location = useLocation();
  return (
    <>
      <button onClick={toggle} className="lg:hidden fixed top-20 left-4 z-40 bg-panel border border-border p-2">
        <Menu className="w-5 h-5" />
      </button>
      <aside className={`fixed top-16 left-0 bottom-0 w-64 bg-surface border-r border-border z-30 transition-transform overflow-y-auto ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <nav className="p-4 space-y-1 mt-4">
          {adminNav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm font-body transition-colors ${
                location.pathname === item.to ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-panel"
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </Link>
          ))}
        </nav>
      </aside>
    </>
  );
};

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60) || "studio";

const DEFAULT_SEED: Array<Partial<StudioRow>> = [
  {
    name: "Studio A", slug: "studio-a", hourly_rate: 35, min_hours: 1,
    tagline: "General photo studio with strobe", capacity: 6,
    features: ["Profoto strobe", "V-flats", "Backdrops"] as any,
    amenities: ["WiFi", "AC", "Restroom"] as any,
    images: [] as any, active: true, display_order: 0,
  },
  {
    name: "Studio B", slug: "studio-b", hourly_rate: 60, min_hours: 2,
    tagline: "Hybrid photo/video with cyc wall", capacity: 12,
    features: ["Cyc wall", "Strobe + continuous", "+$15 maintenance fee"] as any,
    amenities: ["WiFi", "AC", "Green room"] as any,
    images: [] as any, active: true, display_order: 1,
  },
  {
    name: "Beauty Station", slug: "beauty-station", hourly_rate: 25, min_hours: 1,
    tagline: "6×6 beauty area", capacity: 2,
    features: ["Ring light", "Mirror", "Stool"] as any,
    amenities: ["WiFi", "AC"] as any,
    images: [] as any, active: true, display_order: 2,
  },
];

type Draft = {
  id?: string;
  name: string;
  slug: string;
  tagline: string;
  description: string;
  hourly_rate: number;
  min_hours: number;
  capacity: number | null;
  square_feet: number | null;
  features: string;
  amenities: string;
  images: ImageItem[];
  active: boolean;
  display_order: number;
};

const emptyDraft: Draft = {
  name: "", slug: "", tagline: "", description: "",
  hourly_rate: 0, min_hours: 1, capacity: null, square_feet: null,
  features: "", amenities: "", images: [], active: true, display_order: 0,
};

const toLines = (v: unknown): string => {
  if (Array.isArray(v)) return v.map((x) => String(x)).join("\n");
  if (v == null) return "";
  return String(v);
};
const fromLines = (s: string): string[] =>
  s.split("\n").map((l) => l.trim()).filter(Boolean);

const normalizeImages = (raw: unknown): ImageItem[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((it) => {
    if (typeof it === "string") return { url: it };
    if (it && typeof it === "object" && typeof (it as any).url === "string") {
      const o = it as any;
      return {
        url: o.url,
        thumb_url: o.thumb_url || undefined,
        alt: o.alt || undefined,
        title: o.title || undefined,
        category: IMAGE_CATEGORIES.includes(o.category) ? o.category : undefined,
        featured: !!o.featured,
        variants: Array.isArray(o.variants)
          ? o.variants
              .filter((v: any) => v && typeof v.url === "string" && Number(v.width) > 0)
              .map((v: any) => ({ url: v.url, width: Number(v.width), type: v.type || undefined }))
          : undefined,
      };
    }
    return null;
  }).filter(Boolean) as ImageItem[];
};

const rowToDraft = (r: StudioRow): Draft => ({
  id: r.id, name: r.name, slug: r.slug,
  tagline: r.tagline ?? "", description: r.description ?? "",
  hourly_rate: Number(r.hourly_rate ?? 0),
  min_hours: Number((r as any).min_hours ?? 1),
  capacity: r.capacity, square_feet: r.square_feet,
  features: toLines(r.features), amenities: toLines((r as any).amenities),
  images: normalizeImages(r.images),
  active: r.active, display_order: r.display_order,
});

// Resize an image to a max dimension, returning a Blob with explicit type.
const resizeToBlob = (
  file: File | Blob,
  maxDim: number,
  type: "image/webp" | "image/jpeg" = "image/webp",
  quality = 0.82,
): Promise<{ blob: Blob; width: number; height: number } | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * ratio));
      const h = Math.max(1, Math.round(img.height * ratio));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) { URL.revokeObjectURL(url); resolve(null); return; }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((b) => {
        URL.revokeObjectURL(url);
        resolve(b ? { blob: b, width: w, height: h } : null);
      }, type, quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

const RESPONSIVE_WIDTHS = [400, 800, 1600] as const;
const THUMB_WIDTH = 480;

// Extract storage path inside the `studios` bucket from a public URL.
const pathFromPublicUrl = (url: string): string | null => {
  const m = url.match(/\/storage\/v1\/object\/public\/studios\/(.+)$/);
  return m ? decodeURIComponent(m[1]) : null;
};

const buildSrcSet = (img: ImageItem): { srcSet?: string; sizes?: string } => {
  const v = (img.variants || []).filter((x) => x.width > 0);
  if (!v.length) return {};
  const srcSet = v.slice().sort((a, b) => a.width - b.width)
    .map((x) => `${x.url} ${x.width}w`).join(", ");
  return { srcSet, sizes: "(max-width: 768px) 100vw, 800px" };
};

const AdminStudios = () => {
  const [rows, setRows] = useState<StudioRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const imageFileRef = useRef<HTMLInputElement>(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [preview, setPreview] = useState<ImageItem | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkCategory, setBulkCategory] = useState<ImageCategory>("Interior");
  const [bulkTitle, setBulkTitle] = useState("");
  const [bulkAlt, setBulkAlt] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<{ kind: "single"; index: number } | { kind: "bulk" } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const updateImage = (idx: number, patch: Partial<ImageItem>) => {
    setDraft((d) => {
      const next = d.images.slice();
      next[idx] = { ...next[idx], ...patch };
      return { ...d, images: next };
    });
  };

  const setFeatured = (idx: number) => {
    setDraft((d) => ({
      ...d,
      images: d.images.map((it, i) => ({ ...it, featured: i === idx })),
    }));
  };

  const clearFeatured = () => {
    setDraft((d) => ({ ...d, images: d.images.map((it) => ({ ...it, featured: false })) }));
  };

  const deleteFromStorage = async (img: ImageItem) => {
    const paths: string[] = [];
    const main = pathFromPublicUrl(img.url);
    if (main) paths.push(main);
    if (img.thumb_url) {
      const t = pathFromPublicUrl(img.thumb_url);
      if (t) paths.push(t);
    }
    for (const v of img.variants || []) {
      const p = pathFromPublicUrl(v.url);
      if (p) paths.push(p);
    }
    if (paths.length) {
      const { error } = await supabase.storage.from("studios").remove(paths);
      if (error) console.warn("Storage remove failed:", error.message);
    }
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const indices =
        confirmDelete.kind === "single" ? [confirmDelete.index] : Array.from(selected);
      const targets = indices.map((i) => draft.images[i]).filter(Boolean) as ImageItem[];
      await Promise.all(targets.map(deleteFromStorage));
      setDraft((d) => ({ ...d, images: d.images.filter((_, i) => !indices.includes(i)) }));
      setSelected(new Set());
      toast.success(`Deleted ${targets.length} image${targets.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  const toggleSelect = (idx: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  const toggleSelectAll = () =>
    setSelected((prev) => prev.size === draft.images.length ? new Set() : new Set(draft.images.map((_, i) => i)));

  const bulkApply = (patch: Partial<ImageItem>) => {
    if (selected.size === 0) { toast.info("Select images first"); return; }
    setDraft((d) => ({
      ...d,
      images: d.images.map((it, i) => selected.has(i) ? { ...it, ...patch } : it),
    }));
  };

  const featuredCount = draft.images.filter((i) => i.featured).length;

  const onDragStart = (idx: number) => setDragIdx(idx);
  const onDragOver = (e: React.DragEvent) => e.preventDefault();
  const onDrop = (idx: number) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); return; }
    setDraft((d) => {
      const next = d.images.slice();
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return { ...d, images: next };
    });
    setDragIdx(null);
  };

  const uploadImages = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadingImg(true);
    const added: ImageItem[] = [];
    try {
      for (const file of Array.from(files)) {
        const safeName = file.name.replace(/[^a-z0-9.-]/gi, "_");
        const baseName = safeName.replace(/\.[^.]+$/, "");
        const stamp = Date.now();
        const folder = draft.slug || "studio";

        // Original (preserve source)
        const fullPath = `${folder}/${stamp}-${safeName}`;
        const { error: upErr } = await supabase.storage.from("studios").upload(fullPath, file, { upsert: false });
        if (upErr) { toast.error(`Upload failed: ${upErr.message}`); continue; }
        const fullUrl = supabase.storage.from("studios").getPublicUrl(fullPath).data.publicUrl;

        // Responsive WebP variants
        const variants: { url: string; width: number; type?: string }[] = [];
        for (const w of RESPONSIVE_WIDTHS) {
          const r = await resizeToBlob(file, w, "image/webp", 0.82);
          if (!r) continue;
          const p = `${folder}/variants/${stamp}-${baseName}-${r.width}w.webp`;
          const { error } = await supabase.storage
            .from("studios").upload(p, r.blob, { upsert: false, contentType: "image/webp" });
          if (!error) {
            variants.push({
              url: supabase.storage.from("studios").getPublicUrl(p).data.publicUrl,
              width: r.width, type: "image/webp",
            });
          }
        }

        // WebP thumbnail
        let thumbUrl: string | undefined;
        const thumbR = await resizeToBlob(file, THUMB_WIDTH, "image/webp", 0.8);
        if (thumbR) {
          const thumbPath = `${folder}/thumbs/${stamp}-${baseName}.webp`;
          const { error: tErr } = await supabase.storage
            .from("studios")
            .upload(thumbPath, thumbR.blob, { upsert: false, contentType: "image/webp" });
          if (!tErr) thumbUrl = supabase.storage.from("studios").getPublicUrl(thumbPath).data.publicUrl;
        }

        added.push({
          url: fullUrl,
          thumb_url: thumbUrl,
          alt: file.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " "),
          title: "",
          category: "Interior",
          featured: false,
          variants: variants.length ? variants : undefined,
        });
      }
      if (added.length) {
        setDraft((d) => {
          const merged = [...d.images, ...added];
          // ensure exactly one featured if none yet
          if (!merged.some((i) => i.featured) && merged.length > 0) merged[0].featured = true;
          return { ...d, images: merged };
        });
        toast.success(`Uploaded ${added.length} image${added.length === 1 ? "" : "s"}`);
      }
    } finally {
      setUploadingImg(false);
      if (imageFileRef.current) imageFileRef.current.value = "";
    }
  };

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("studios").select("*")
      .order("display_order", { ascending: true })
      .order("name", { ascending: true });
    if (error) toast.error(error.message);
    setRows((data ?? []) as StudioRow[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setDraft({ ...emptyDraft, display_order: rows.length }); setOpen(true); };
  const openEdit = (r: StudioRow) => { setDraft(rowToDraft(r)); setOpen(true); loadStudioAddons(r.id); };

  // ---- Per-studio add-ons -----------------------------------------------
  const [sAddons, setSAddons] = useState<AddonRow[]>([]);
  const [sAddonsLoading, setSAddonsLoading] = useState(false);
  const [newAddon, setNewAddon] = useState<{ name: string; price: string; max_quantity: string }>({ name: "", price: "", max_quantity: "" });

  const loadStudioAddons = async (studioId: string) => {
    setSAddonsLoading(true);
    const { data, error } = await supabase
      .from("addons")
      .select("*")
      .eq("studio_id", studioId)
      .order("display_order")
      .order("created_at");
    if (error) toast.error(error.message);
    setSAddons((data ?? []) as AddonRow[]);
    setSAddonsLoading(false);
  };

  const addStudioAddon = async () => {
    if (!draft.id) return toast.error("Save the studio first");
    const names = newAddon.name
      .split(/[,\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const price = Number(newAddon.price);
    if (names.length === 0) return toast.error("Add-on name required");
    if (!Number.isFinite(price) || price < 0) return toast.error("Valid price required");
    const maxQ = newAddon.max_quantity ? Math.max(1, parseInt(newAddon.max_quantity, 10)) : null;
    const rows = names.map((name, i) => ({
      name,
      price,
      studio_id: draft.id!,
      max_quantity: maxQ,
      pricing_unit: "flat",
      active: true,
      display_order: sAddons.length + i,
    }));
    const { error } = await supabase.from("addons").insert(rows);
    if (error) return toast.error(error.message);
    setNewAddon({ name: "", price: "", max_quantity: "" });
    toast.success(`Added ${rows.length} add-on${rows.length === 1 ? "" : "s"}`);
    loadStudioAddons(draft.id);
  };

  // ---- Drag-and-drop reordering for studio add-ons ----------------------
  const [addonDragIdx, setAddonDragIdx] = useState<number | null>(null);

  const reorderStudioAddons = async (from: number, to: number) => {
    if (from === to) return;
    const next = sAddons.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setSAddons(next);
    // Persist new display_order for each affected row
    await Promise.all(
      next.map((a, i) =>
        a.display_order === i
          ? Promise.resolve()
          : supabase.from("addons").update({ display_order: i }).eq("id", a.id),
      ),
    );
  };

  const toggleStudioAddon = async (a: AddonRow) => {
    const { error } = await supabase.from("addons").update({ active: !a.active }).eq("id", a.id);
    if (error) return toast.error(error.message);
    if (draft.id) loadStudioAddons(draft.id);
  };

  const deleteStudioAddon = async (id: string) => {
    if (!confirm("Delete this add-on?")) return;
    const { error } = await supabase.from("addons").delete().eq("id", id);
    if (error) return toast.error(error.message);
    if (draft.id) loadStudioAddons(draft.id);
  };

  const save = async () => {
    if (!draft.name.trim()) { toast.error("Name is required"); return; }
    if (draft.images.filter((i) => i.featured).length > 1) {
      toast.error("Only one image can be marked featured");
      return;
    }
    const slug = (draft.slug.trim() || slugify(draft.name));
    setSaving(true);
    // enforce single featured
    const featuredIdx = draft.images.findIndex((i) => i.featured);
    const cleanImages: ImageItem[] = draft.images.map((it, i) => ({
      url: it.url,
      thumb_url: it.thumb_url,
      alt: it.alt?.trim() || undefined,
      title: it.title?.trim() || undefined,
      category: it.category,
      featured: featuredIdx === -1 ? i === 0 : i === featuredIdx,
      variants: it.variants && it.variants.length ? it.variants : undefined,
    }));
    const payload = {
      name: draft.name.trim(),
      slug,
      tagline: draft.tagline.trim() || null,
      description: draft.description.trim() || null,
      hourly_rate: Number(draft.hourly_rate) || 0,
      min_hours: Math.max(1, Number(draft.min_hours) || 1),
      capacity: draft.capacity ?? null,
      square_feet: draft.square_feet ?? null,
      features: fromLines(draft.features),
      amenities: fromLines(draft.amenities),
      images: cleanImages as any,
      active: draft.active,
      display_order: Number(draft.display_order) || 0,
    };
    const q = draft.id
      ? supabase.from("studios").update(payload).eq("id", draft.id)
      : supabase.from("studios").insert(payload);
    const { error } = await q;
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(draft.id ? "Studio saved" : "Studio created");
    setOpen(false);
    load();
  };

  const toggleActive = async (r: StudioRow) => {
    const { error } = await supabase.from("studios").update({ active: !r.active }).eq("id", r.id);
    if (error) return toast.error(error.message);
    toast.success(r.active ? "Deactivated" : "Activated");
    load();
  };

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const { data: existing } = await supabase.from("studios").select("slug,name");
      const have = new Set([
        ...(existing ?? []).map((s: any) => String(s.slug)),
        ...(existing ?? []).map((s: any) => String(s.name).toLowerCase().trim()),
      ]);
      const toInsert = DEFAULT_SEED.filter(
        (s) => !have.has(String(s.slug)) && !have.has(String(s.name).toLowerCase().trim())
      );
      if (toInsert.length === 0) {
        toast.info("Defaults already seeded");
      } else {
        const { error } = await supabase.from("studios").insert(toInsert as any);
        if (error) throw error;
        toast.success(`Seeded ${toInsert.length} default studio${toInsert.length === 1 ? "" : "s"}`);
      }
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSeeding(false);
    }
  };

  const handleCsv = async (file: File) => {
    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
      if (lines.length < 2) throw new Error("CSV must have a header row and at least one data row");
      const parseRow = (row: string): string[] => {
        const out: string[] = []; let cur = ""; let inQ = false;
        for (let i = 0; i < row.length; i++) {
          const c = row[i];
          if (inQ) {
            if (c === '"' && row[i + 1] === '"') { cur += '"'; i++; }
            else if (c === '"') inQ = false;
            else cur += c;
          } else {
            if (c === ',') { out.push(cur); cur = ""; }
            else if (c === '"') inQ = true;
            else cur += c;
          }
        }
        out.push(cur);
        return out.map((s) => s.trim());
      };
      const header = parseRow(lines[0]).map((h) => h.toLowerCase().trim());
      const need = ["name", "hourly_rate"];
      for (const n of need) if (!header.includes(n)) throw new Error(`CSV missing required column: ${n}`);

      const idx = (k: string) => header.indexOf(k);
      const splitList = (s: string) => s.split(/[|;]/).map((x) => x.trim()).filter(Boolean);

      const { data: existing } = await supabase.from("studios").select("slug,name");
      const have = new Set([
        ...(existing ?? []).map((s: any) => String(s.slug)),
        ...(existing ?? []).map((s: any) => String(s.name).toLowerCase().trim()),
      ]);

      const records: any[] = [];
      let skipped = 0;
      for (let i = 1; i < lines.length; i++) {
        const cols = parseRow(lines[i]);
        const name = cols[idx("name")] ?? "";
        if (!name) { skipped++; continue; }
        const slugRaw = idx("slug") >= 0 ? cols[idx("slug")] : "";
        const slug = (slugRaw || slugify(name));
        if (have.has(slug) || have.has(name.toLowerCase().trim())) { skipped++; continue; }
        have.add(slug); have.add(name.toLowerCase().trim());
        records.push({
          name, slug,
          tagline: idx("tagline") >= 0 ? (cols[idx("tagline")] || null) : null,
          description: idx("description") >= 0 ? (cols[idx("description")] || null) : null,
          hourly_rate: Number(cols[idx("hourly_rate")] || 0) || 0,
          min_hours: idx("min_hours") >= 0 ? Math.max(1, Number(cols[idx("min_hours")] || 1) || 1) : 1,
          capacity: idx("capacity") >= 0 && cols[idx("capacity")] ? Number(cols[idx("capacity")]) : null,
          square_feet: idx("square_feet") >= 0 && cols[idx("square_feet")] ? Number(cols[idx("square_feet")]) : null,
          features: idx("features") >= 0 ? splitList(cols[idx("features")] || "") : [],
          amenities: idx("amenities") >= 0 ? splitList(cols[idx("amenities")] || "") : [],
          images: idx("images") >= 0 ? splitList(cols[idx("images")] || "").map((u) => ({ url: u })) : [],
          active: idx("active") >= 0 ? !/^(false|0|no)$/i.test(cols[idx("active")] || "true") : true,
          display_order: idx("display_order") >= 0 ? Number(cols[idx("display_order")] || 0) : i,
        });
      }
      if (records.length === 0) {
        toast.info(`No new studios to import (skipped ${skipped} duplicate${skipped === 1 ? "" : "s"})`);
      } else {
        const { error } = await supabase.from("studios").insert(records);
        if (error) throw error;
        toast.success(`Imported ${records.length} studio${records.length === 1 ? "" : "s"} (skipped ${skipped})`);
      }
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="pt-16 min-h-screen">
      <Sidebar open={sidebarOpen} toggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="lg:ml-64 p-6 lg:p-10">
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <h1 className="text-3xl font-display tracking-widest">STUDIOS</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={seedDefaults} disabled={seeding} className="gap-2">
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Seed default studios
            </Button>
            <input
              ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCsv(f); }}
            />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="gap-2">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Import CSV
            </Button>
            <Button onClick={openNew} className="gap-2">
              <Plus className="w-4 h-4" /> New studio
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mb-6 max-w-2xl">
          CSV columns: <span className="font-mono">name,hourly_rate</span> required.
          Optional: <span className="font-mono">slug, tagline, description, min_hours, capacity, square_feet, features, amenities, images, active, display_order</span>.
          Use <span className="font-mono">|</span> to separate list values. Duplicates by name or slug are skipped.
        </p>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : rows.length === 0 ? (
          <Card className="p-10 bg-surface border-border text-center">
            <Building2 className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-display tracking-wider mb-2">No studios yet</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Seed the standard MyStudio spaces in one click, or create your first studio manually.
            </p>
            <div className="flex justify-center gap-2">
              <Button onClick={seedDefaults} disabled={seeding} className="gap-2">
                {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Seed default studios
              </Button>
              <Button variant="outline" onClick={openNew} className="gap-2">
                <Plus className="w-4 h-4" /> New studio
              </Button>
            </div>
          </Card>
        ) : (
          <div className="border border-border divide-y divide-border bg-surface">
            <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-background text-xs uppercase tracking-wider text-muted-foreground">
              <div className="col-span-4">Name</div>
              <div className="col-span-2">Hourly</div>
              <div className="col-span-2">Min hrs</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {rows.map((r) => (
              <div key={r.id} className="grid grid-cols-12 gap-3 px-4 py-3 items-center text-sm">
                <div className="col-span-4">
                  <div className="font-display tracking-wider">{r.name}</div>
                  <div className="text-xs font-mono text-muted-foreground">{r.slug}</div>
                </div>
                <div className="col-span-2 font-mono">${Number(r.hourly_rate).toFixed(0)}/hr</div>
                <div className="col-span-2 font-mono">{(r as any).min_hours ?? 1}h</div>
                <div className="col-span-2">
                  <Badge variant={r.active ? "default" : "outline"}>{r.active ? "active" : "inactive"}</Badge>
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(r)} className="gap-1">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggleActive(r)}>
                    {r.active ? "Deactivate" : "Activate"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{draft.id ? "Edit studio" : "New studio"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Name</Label>
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({
                    ...draft, name: e.target.value,
                    slug: draft.id ? draft.slug : slugify(e.target.value),
                  })}
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Slug</Label>
                <Input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Tagline</Label>
              <Input value={draft.tagline} onChange={(e) => setDraft({ ...draft, tagline: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Description</Label>
              <Textarea rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Hourly $</Label>
                <Input type="number" min={0} value={draft.hourly_rate}
                  onChange={(e) => setDraft({ ...draft, hourly_rate: Number(e.target.value) })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Min hours</Label>
                <Input type="number" min={1} value={draft.min_hours}
                  onChange={(e) => setDraft({ ...draft, min_hours: Number(e.target.value) })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Capacity</Label>
                <Input type="number" min={0} value={draft.capacity ?? ""}
                  onChange={(e) => setDraft({ ...draft, capacity: e.target.value ? Number(e.target.value) : null })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Sq ft</Label>
                <Input type="number" min={0} value={draft.square_feet ?? ""}
                  onChange={(e) => setDraft({ ...draft, square_feet: e.target.value ? Number(e.target.value) : null })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Features (one per line)</Label>
                <Textarea rows={5} value={draft.features} onChange={(e) => setDraft({ ...draft, features: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Amenities (one per line)</Label>
                <Textarea rows={5} value={draft.amenities} onChange={(e) => setDraft({ ...draft, amenities: e.target.value })} />
              </div>
            </div>

            {/* Image manager */}
            <div className="border border-border p-3 bg-background/40">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Images</Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Drag rows to reorder. Click a thumbnail to preview full size. One image can be marked featured for the studio card.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {draft.images.some((i) => i.featured) && (
                    <Button type="button" size="sm" variant="ghost" onClick={clearFeatured} className="gap-1 text-xs">
                      <StarOff className="w-3.5 h-3.5" /> Clear featured
                    </Button>
                  )}
                  <input
                    ref={imageFileRef} type="file" accept="image/*" multiple className="hidden"
                    onChange={(e) => uploadImages(e.target.files)}
                  />
                  <Button type="button" size="sm" variant="outline" onClick={() => imageFileRef.current?.click()} disabled={uploadingImg} className="gap-2">
                    {uploadingImg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    Upload
                  </Button>
                </div>
              </div>

              {featuredCount > 1 && (
                <Alert variant="destructive" className="mb-3">
                  <AlertDescription className="text-xs">
                    {featuredCount} images are marked featured. Only one image can be featured — unstar the extras before saving.
                  </AlertDescription>
                </Alert>
              )}

              {draft.images.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-3 p-2 border border-border bg-background/60">
                  <label className="flex items-center gap-2 text-xs">
                    <Checkbox
                      checked={selected.size > 0 && selected.size === draft.images.length}
                      onCheckedChange={toggleSelectAll}
                    />
                    <span>{selected.size} selected</span>
                  </label>
                  <div className="h-4 w-px bg-border" />
                  <Select value={bulkCategory} onValueChange={(v) => setBulkCategory(v as ImageCategory)}>
                    <SelectTrigger className="h-8 text-xs w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {IMAGE_CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" size="sm" variant="outline" className="h-8 text-xs"
                    onClick={() => bulkApply({ category: bulkCategory })}>
                    Apply category
                  </Button>
                  <Input placeholder="Bulk title" value={bulkTitle}
                    onChange={(e) => setBulkTitle(e.target.value)} className="h-8 text-xs w-40" />
                  <Button type="button" size="sm" variant="outline" className="h-8 text-xs"
                    onClick={() => bulkApply({ title: bulkTitle })}>Apply title</Button>
                  <Input placeholder="Bulk alt text" value={bulkAlt}
                    onChange={(e) => setBulkAlt(e.target.value)} className="h-8 text-xs w-44" />
                  <Button type="button" size="sm" variant="outline" className="h-8 text-xs"
                    onClick={() => bulkApply({ alt: bulkAlt })}>Apply alt</Button>
                  <Button type="button" size="sm" variant="ghost" className="h-8 text-xs gap-1"
                    onClick={() => bulkApply({ featured: false })}>
                    <StarOff className="w-3.5 h-3.5" /> Clear featured
                  </Button>
                  <Button type="button" size="sm" variant="destructive" className="h-8 text-xs gap-1 ml-auto"
                    disabled={selected.size === 0}
                    onClick={() => setConfirmDelete({ kind: "bulk" })}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete selected
                  </Button>
                </div>
              )}

              {draft.images.length === 0 ? (
                <div className="text-xs text-muted-foreground text-center py-6 border border-dashed border-border">
                  No images yet — upload photos to populate this studio's gallery.
                </div>
              ) : (
                <div className="space-y-2">
                  {draft.images.map((img, i) => (
                    <div
                      key={`${img.url}-${i}`}
                      draggable
                      onDragStart={() => onDragStart(i)}
                      onDragOver={onDragOver}
                      onDrop={() => onDrop(i)}
                      className={`grid grid-cols-12 gap-2 items-center p-2 border bg-surface ${
                        dragIdx === i ? "opacity-50 border-primary" : "border-border"
                      }`}
                    >
                      <div className="col-span-1 flex items-center justify-center gap-1 text-muted-foreground">
                        <Checkbox
                          checked={selected.has(i)}
                          onCheckedChange={() => toggleSelect(i)}
                        />
                        <GripVertical className="w-4 h-4 cursor-grab" />
                      </div>
                      <button
                        type="button"
                        onClick={() => setPreview(img)}
                        className="col-span-2 aspect-square overflow-hidden border border-border bg-background"
                        title="Click to preview"
                      >
                        <img
                          src={img.thumb_url || img.url}
                          alt={img.alt || ""}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                          {...buildSrcSet(img)}
                        />
                      </button>
                      <div className="col-span-8 grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Title"
                          value={img.title || ""}
                          onChange={(e) => updateImage(i, { title: e.target.value })}
                          className="h-8 text-xs"
                        />
                        <Select
                          value={img.category || "Interior"}
                          onValueChange={(v) => updateImage(i, { category: v as ImageCategory })}
                        >
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {IMAGE_CATEGORIES.map((c) => (
                              <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Alt text (accessibility / SEO)"
                          value={img.alt || ""}
                          onChange={(e) => updateImage(i, { alt: e.target.value })}
                          className="h-8 text-xs col-span-2"
                        />
                      </div>
                      <div className="col-span-1 flex flex-col items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setFeatured(i)}
                          title={img.featured ? "Featured" : "Make featured"}
                          className={`p-1 ${img.featured ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
                        >
                          <Star className={`w-4 h-4 ${img.featured ? "fill-current" : ""}`} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete({ kind: "single", index: i })}
                          title="Remove"
                          className="p-1 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <Switch checked={draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: v })} />
                <span className="text-sm">Active</span>
              </label>
              <div className="flex items-center gap-2">
                <Label className="text-xs uppercase tracking-wider">Order</Label>
                <Input type="number" className="w-20" value={draft.display_order}
                  onChange={(e) => setDraft({ ...draft, display_order: Number(e.target.value) })} />
              </div>
            </div>

            {/* Per-studio add-ons (booking attributes) */}
            <div className="border border-border p-3 bg-background/40">
              <div className="mb-3">
                <Label className="text-xs uppercase tracking-wider">Booking add-ons</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Optional paid extras customers can select with this studio (e.g. Backdrop color +$10, Throne chair +$65).
                </p>
              </div>
              {!draft.id ? (
                <p className="text-xs text-muted-foreground">Save the studio first to add booking add-ons.</p>
              ) : (
                <>
                  {sAddonsLoading ? (
                    <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin" /></div>
                  ) : sAddons.length === 0 ? (
                    <p className="text-xs text-muted-foreground mb-3">No add-ons yet.</p>
                  ) : (
                    <div className="border border-border divide-y divide-border mb-3">
                      {sAddons.map((a) => (
                        <div
                          key={a.id}
                          draggable
                          onDragStart={() => setAddonDragIdx(sAddons.indexOf(a))}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => {
                            if (addonDragIdx === null) return;
                            reorderStudioAddons(addonDragIdx, sAddons.indexOf(a));
                            setAddonDragIdx(null);
                          }}
                          className={`grid grid-cols-12 gap-2 px-3 py-2 items-center text-sm cursor-grab ${addonDragIdx === sAddons.indexOf(a) ? "opacity-50 bg-primary/5" : ""}`}
                        >
                          <div className="col-span-5 flex items-center gap-2">
                            <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />
                            <div>
                            <div className="font-body">{a.name}</div>
                            {a.max_quantity ? (
                              <div className="text-[11px] text-muted-foreground">Max qty: {a.max_quantity}</div>
                            ) : null}
                            </div>
                          </div>
                          <div className="col-span-3 font-mono text-xs">${Number(a.price).toFixed(2)}</div>
                          <div className="col-span-2">
                            <Badge variant={a.active ? "default" : "outline"}>{a.active ? "active" : "hidden"}</Badge>
                          </div>
                          <div className="col-span-2 flex justify-end gap-1">
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => toggleStudioAddon(a)}>
                              {a.active ? "Hide" : "Show"}
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => deleteStudioAddon(a.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <Label className="text-[11px] uppercase tracking-wider">Name</Label>
                      <Input value={newAddon.name} placeholder="Backdrop color  (or: pink, royal blue, navy…)"
                        onChange={(e) => setNewAddon({ ...newAddon, name: e.target.value })} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[11px] uppercase tracking-wider">Price $</Label>
                      <Input type="number" min={0} step="0.01" value={newAddon.price} placeholder="10"
                        onChange={(e) => setNewAddon({ ...newAddon, price: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[11px] uppercase tracking-wider">Max qty</Label>
                      <Input type="number" min={1} value={newAddon.max_quantity} placeholder="—"
                        onChange={(e) => setNewAddon({ ...newAddon, max_quantity: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <Button type="button" size="sm" className="w-full gap-1" onClick={addStudioAddon}>
                        <Plus className="w-3.5 h-3.5" /> Add
                      </Button>
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2">
                    Drag rows to reorder. Paste comma- or newline-separated names to create multiple add-ons at once (they'll share the price & max qty above).
                  </p>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {draft.id ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-sm font-mono truncate">
              {preview?.title || preview?.alt || "Image preview"}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-4">
              <div className="bg-background border border-border flex items-center justify-center max-h-[70vh] overflow-hidden">
                <img
                  src={preview.url}
                  alt={preview.alt || ""}
                  className="max-w-full max-h-[70vh] object-contain"
                  loading="lazy"
                  decoding="async"
                  {...buildSrcSet(preview)}
                />
              </div>
              <div className="space-y-2 text-xs">
                <div>
                  <div className="uppercase tracking-wider text-muted-foreground mb-1">Thumbnail</div>
                  <img
                    src={preview.thumb_url || preview.url}
                    alt={preview.alt || ""}
                    className="w-full border border-border"
                    loading="lazy"
                    decoding="async"
                    {...buildSrcSet(preview)}
                  />
                </div>
                {preview.category && (
                  <div><span className="text-muted-foreground">Category:</span> {preview.category}</div>
                )}
                {preview.featured && <Badge>Featured</Badge>}
                {preview.variants && preview.variants.length > 0 && (
                  <div className="text-muted-foreground">
                    Responsive: {preview.variants.map((v) => `${v.width}w`).join(" · ")} (WebP)
                  </div>
                )}
                <div className="break-all">
                  <a href={preview.url} target="_blank" rel="noreferrer" className="text-primary underline">Open original</a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && !deleting && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDelete?.kind === "bulk"
                ? `Delete ${selected.size} image${selected.size === 1 ? "" : "s"}?`
                : "Delete this image?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              The original file, generated thumbnail, and responsive variants will be permanently
              removed from storage. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); performDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminStudios;
